import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Copy, Mail, MessageCircle, Send, Facebook, Check } from "lucide-react";
import { toast } from "sonner";

export const ShareMenu = ({ url, testidPrefix = "share" }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.origin : "https://restaurantejobs.com");
  const message = t("share.message");
  const full = `${message} ${shareUrl}`;

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "RestauranteJobs", text: message, url: shareUrl }); }
      catch (e) { /* cancelled */ }
    } else { setOpen((o) => !o); }
  };

  const openMenu = (e) => { e.stopPropagation(); if (navigator.share) nativeShare(); else setOpen((o) => !o); };

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true); toast.success(t("share.copied"));
    setTimeout(() => setCopied(false), 1500);
  };

  const channels = [
    { key: "whatsapp", label: "WhatsApp", color: "#25D366", icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(full)}` },
    { key: "facebook", label: "Facebook", color: "#1877F2", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { key: "messenger", label: "Messenger", color: "#0084FF", icon: MessageCircle, href: `https://www.facebook.com/dialog/send?app_id=1&link=${encodeURIComponent(shareUrl)}&redirect_uri=${encodeURIComponent(shareUrl)}` },
    { key: "x", label: "X / Twitter", color: "#000000", icon: Share2, href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(shareUrl)}` },
    { key: "telegram", label: "Telegram", color: "#0088CC", icon: Send, href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(message)}` },
    { key: "email", label: t("share.email"), color: "#D95A41", icon: Mail, href: `mailto:?subject=${encodeURIComponent("RestauranteJobs")}&body=${encodeURIComponent(full)}` },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={`${testidPrefix}-btn`}
        onClick={openMenu}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand text-white text-sm font-semibold hover:bg-brand-hover hover:-translate-y-0.5 transition-all"
      >
        <Share2 className="w-4 h-4" /> {t("share.button")}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl border border-stone-border shadow-xl p-2 z-50" data-testid={`${testidPrefix}-menu`}>
          <p className="text-xs font-semibold text-stone-muted px-3 py-2">{t("share.title")}</p>
          {channels.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.key}
                data-testid={`${testidPrefix}-${c.key}`}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-stone-bg transition-colors"
              >
                <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${c.color}1A` }}>
                  <Icon className="w-4 h-4" style={{ color: c.color }} />
                </span>
                <span className="text-sm font-medium text-stone-text">{c.label}</span>
              </a>
            );
          })}
          <button
            data-testid={`${testidPrefix}-copy`}
            onClick={copy}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-stone-bg transition-colors"
          >
            <span className="w-8 h-8 rounded-full bg-stone-bg flex items-center justify-center">
              {copied ? <Check className="w-4 h-4 text-housing" /> : <Copy className="w-4 h-4 text-stone-sub" />}
            </span>
            <span className="text-sm font-medium text-stone-text">{t("share.copy")}</span>
          </button>
        </div>
      )}
    </div>
  );
};
